import type { ValueChainEdge, ValueChainGraph, ValueChainNode } from '@/types/valueChain';

export type TreeIndex = {
  byId: Map<string, ValueChainNode>;
  children: Map<string, string[]>;
  parent: Map<string, string>;
  inboundBom: Map<string, ValueChainEdge>;
  companiesByNode: Map<string, string[]>;
  suppliedNodes: Map<string, string[]>;
  subtreeSize: Map<string, number>;
  depthFromRoot: Map<string, number>;
  maxDepth: number;
  partCount: number;
  companyCount: number;
  mappedRelationships: number;
};

function sortChildren(a: ValueChainEdge, b: ValueChainEdge): number {
  const ar = a.rank ?? Number.POSITIVE_INFINITY;
  const br = b.rank ?? Number.POSITIVE_INFINITY;
  if (ar !== br) return ar - br;
  return a.id.localeCompare(b.id);
}

export function buildTreeIndex(graph: ValueChainGraph): TreeIndex {
  const byId = new Map(graph.nodes.map((node) => [node.id, node]));
  const children = new Map<string, string[]>();
  const parent = new Map<string, string>();
  const inboundBom = new Map<string, ValueChainEdge>();
  const companiesByNode = new Map<string, string[]>();
  const suppliedNodes = new Map<string, string[]>();

  const bomEdges: ValueChainEdge[] = [];
  const supplyEdges: ValueChainEdge[] = [];
  graph.edges.forEach((edge) => {
    if (edge.supplyChain || edge.relation === 'MANUFACTURED_BY' || edge.relation === 'SUPPLIES') {
      supplyEdges.push(edge);
    } else {
      bomEdges.push(edge);
    }
  });

  bomEdges.sort(sortChildren).forEach((edge) => {
    if (!byId.has(edge.from) || !byId.has(edge.to)) return;
    const list = children.get(edge.from) ?? [];
    if (!list.includes(edge.to)) list.push(edge.to);
    children.set(edge.from, list);
    if (!parent.has(edge.to)) parent.set(edge.to, edge.from);
    if (!inboundBom.has(edge.to)) inboundBom.set(edge.to, edge);
  });

  supplyEdges.forEach((edge) => {
    if (!byId.has(edge.from) || !byId.has(edge.to)) return;
    const companyId = byId.get(edge.to)?.type === 'Company' ? edge.to : edge.from;
    const partId = companyId === edge.to ? edge.from : edge.to;
    const companies = companiesByNode.get(partId) ?? [];
    if (!companies.includes(companyId)) companies.push(companyId);
    companiesByNode.set(partId, companies);
    const parts = suppliedNodes.get(companyId) ?? [];
    if (!parts.includes(partId)) parts.push(partId);
    suppliedNodes.set(companyId, parts);
  });

  const subtreeSize = new Map<string, number>();
  const visiting = new Set<string>();
  const walkSize = (id: string): number => {
    const cached = subtreeSize.get(id);
    if (cached != null) return cached;
    if (visiting.has(id)) return 1;
    visiting.add(id);
    const kids = children.get(id) ?? [];
    const size = 1 + kids.reduce((sum, child) => sum + walkSize(child), 0);
    visiting.delete(id);
    subtreeSize.set(id, size);
    return size;
  };
  graph.nodes.forEach((node) => {
    if (node.type !== 'Company') walkSize(node.id);
  });

  const depthFromRoot = new Map<string, number>();
  const queue = [graph.rootId];
  depthFromRoot.set(graph.rootId, 0);
  let maxDepth = 0;
  while (queue.length) {
    const id = queue.shift();
    if (!id) break;
    const depth = depthFromRoot.get(id) ?? 0;
    maxDepth = Math.max(maxDepth, depth);
    (children.get(id) ?? []).forEach((child) => {
      if (depthFromRoot.has(child)) return;
      depthFromRoot.set(child, depth + 1);
      queue.push(child);
    });
  }

  const partCount = graph.nodes.filter((node) => node.type !== 'Company').length;
  const companyCount = graph.nodes.filter((node) => node.type === 'Company').length;

  return {
    byId,
    children,
    parent,
    inboundBom,
    companiesByNode,
    suppliedNodes,
    subtreeSize,
    depthFromRoot,
    maxDepth,
    partCount,
    companyCount,
    mappedRelationships: supplyEdges.length,
  };
}

export function ancestorsOf(index: TreeIndex, nodeId: string): ValueChainNode[] {
  const path: ValueChainNode[] = [];
  let current: string | undefined = nodeId;
  const guard = new Set<string>();
  while (current && !guard.has(current)) {
    guard.add(current);
    const node = index.byId.get(current);
    if (node) path.push(node);
    current = index.parent.get(current);
  }
  return path.reverse();
}

export function childrenOf(index: TreeIndex, nodeId: string): ValueChainNode[] {
  return (index.children.get(nodeId) ?? [])
    .map((id) => index.byId.get(id))
    .filter((node): node is ValueChainNode => Boolean(node));
}

export function companiesOf(index: TreeIndex, nodeId: string): ValueChainNode[] {
  return (index.companiesByNode.get(nodeId) ?? [])
    .map((id) => index.byId.get(id))
    .filter((node): node is ValueChainNode => Boolean(node));
}

export function subtreeCompanyCounts(
  index: TreeIndex,
  nodeId: string,
): Array<{ company: ValueChainNode; count: number }> {
  const counts = new Map<string, number>();
  const stack = [nodeId];
  const seen = new Set<string>();
  while (stack.length) {
    const id = stack.pop();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    (index.companiesByNode.get(id) ?? []).forEach((companyId) => {
      counts.set(companyId, (counts.get(companyId) ?? 0) + 1);
    });
    stack.push(...(index.children.get(id) ?? []));
  }
  return [...counts.entries()]
    .map(([id, count]) => {
      const company = index.byId.get(id);
      if (!company) return null;
      return { company, count };
    })
    .filter((row): row is { company: ValueChainNode; count: number } => Boolean(row))
    .sort((a, b) => b.count - a.count);
}

export function subtreeMaterials(
  index: TreeIndex,
  nodeId: string,
): Array<{ node: ValueChainNode; count: number }> {
  const counts = new Map<string, { node: ValueChainNode; count: number }>();
  const stack = [nodeId];
  const seen = new Set<string>();
  while (stack.length) {
    const id = stack.pop();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const node = index.byId.get(id);
    if (node && (node.type === 'Material' || node.type === 'Chemical / Dopant')) {
      const key = node.name.toLowerCase();
      const current = counts.get(key);
      if (current) current.count += 1;
      else counts.set(key, { node, count: 1 });
    }
    stack.push(...(index.children.get(id) ?? []));
  }
  return [...counts.values()].sort((a, b) => b.count - a.count);
}

export function neighborhoodIds(
  index: TreeIndex,
  selectedId: string,
  hops = 2,
): Set<string> {
  const ids = new Set<string>([selectedId]);
  ancestorsOf(index, selectedId).forEach((node) => ids.add(node.id));

  let frontier = [selectedId];
  for (let hop = 0; hop < hops; hop += 1) {
    const next: string[] = [];
    frontier.forEach((id) => {
      (index.children.get(id) ?? []).forEach((child) => {
        if (ids.has(child)) return;
        ids.add(child);
        next.push(child);
      });
      (index.companiesByNode.get(id) ?? []).forEach((companyId) => ids.add(companyId));
    });
    frontier = next;
  }
  return ids;
}

export function unknownSupplierCount(index: TreeIndex, nodeId: string): number {
  return childrenOf(index, nodeId).filter((child) => {
    const companies = companiesOf(index, child.id);
    if (companies.length === 0 && !child.manufacturer) return true;
    const label = (child.manufacturer ?? companies[0]?.name ?? '').toLowerCase();
    return !label || label.includes('unknown');
  }).length;
}
